import { IEvaluatedEmployeePayload, IEvaluationEmployeeRaw } from '../../services/Employee/types'

export interface IEvaluatedEmployeeForm {
  sectorId: string
  employee: IEvaluationEmployeeRaw | null
  nickname: string
  birthdate: string
  /** Asked only when the collaborator's role has no level. */
  organizationalLevel: string
}

export type TEvaluatedEmployeeErrors = Record<string, string[]>

export const EMPTY_EVALUATED_EMPLOYEE: IEvaluatedEmployeeForm = {
  sectorId: '',
  employee: null,
  nickname: '',
  birthdate: '',
  organizationalLevel: ''
}

// Same values as `employees.organizational_level` and the Magenta evaluations.
export const ORGANIZATIONAL_LEVEL_OPTIONS = [
  { value: 'operacional', label: 'Operacional' },
  { value: 'tático', label: 'Tático' },
  { value: 'estratégico', label: 'Estratégico' }
]

export const organizationalLevelLabel = (level: string | null) =>
  ORGANIZATIONAL_LEVEL_OPTIONS.find((option) => option.value === level)?.label ?? level

const ERROR_KEYS: Record<keyof IEvaluatedEmployeeForm, string> = {
  sectorId: 'sector_id',
  employee: 'employee_id',
  nickname: 'nickname',
  birthdate: 'birthdate',
  organizationalLevel: 'organizational_level'
}

/** Drops one key's errors: the page's own fields (manager, feedback type). */
export const withoutError = (errors: TEvaluatedEmployeeErrors, key: string) =>
  Object.fromEntries(Object.entries(errors).filter(([field]) => field !== key))

/** Drops the errors of the fields just changed. */
export const withoutErrorsOf = (errors: TEvaluatedEmployeeErrors, patch: Partial<IEvaluatedEmployeeForm>) => {
  const changed = (Object.keys(patch) as (keyof IEvaluatedEmployeeForm)[]).map((field) => ERROR_KEYS[field])
  return Object.fromEntries(Object.entries(errors).filter(([key]) => !changed.includes(key)))
}

const needsLevel = (form: IEvaluatedEmployeeForm) => form.employee !== null && !form.employee.organizational_level

/** The same checks the API makes, so the page does not send a form it would refuse. */
export const evaluatedEmployeeErrors = (
  form: IEvaluatedEmployeeForm,
  asksPersonal: boolean
): TEvaluatedEmployeeErrors => {
  const errors: TEvaluatedEmployeeErrors = {}
  if (!form.sectorId) errors.sector_id = ['Selecione o setor.']
  if (!form.employee) errors.employee_id = ['Selecione um colaborador da lista.']
  if (asksPersonal && !form.nickname.trim()) errors.nickname = ['Informe como o colaborador gosta de ser chamado.']
  if (asksPersonal && !form.birthdate) errors.birthdate = ['Informe a data de nascimento.']
  if (needsLevel(form) && !form.organizationalLevel) {
    errors.organizational_level = ['Selecione o nível organizacional do cargo.']
  }
  return errors
}

export const evaluatedEmployeePayload = (
  form: IEvaluatedEmployeeForm,
  asksPersonal: boolean
): IEvaluatedEmployeePayload => ({
  sector_id: Number(form.sectorId),
  employee_id: form.employee?.id ?? null,
  ...(asksPersonal ? { nickname: form.nickname.trim(), birthdate: form.birthdate } : {}),
  ...(needsLevel(form) ? { organizational_level: form.organizationalLevel } : {})
})
